import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI, settingsAPI } from '../../services/api';
import * as Icons from 'lucide-react';
import { formatIndianCurrency } from '../../utils/numberFormat';

const tabs = [
  { id: 'profile', label: 'Profile', icon: 'UserCircle' },
  { id: 'contacts', label: 'Contacts', icon: 'Users' },
  { id: 'ip-whitelist', label: 'IP Whitelist', icon: 'Shield' },
  { id: 'security', label: 'API & Security', icon: 'Key' },
];

// ============================================================
// PROFILE TAB
// ============================================================
const ProfileTab = ({ user, updateUser }) => {
  const [profile, setProfile] = useState({ full_name: '', email: '', company_name: '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setProfile({ full_name: user.full_name || '', email: user.email || '', company_name: user.company_name || '' });
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true); setMsg({ type: '', text: '' });
    try {
      await settingsAPI.updateProfile(profile);
      updateUser({ ...user, ...profile });
      setMsg({ type: 'success', text: 'Profile updated successfully' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to update profile' });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      {msg.text && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.type === 'success' ? <Icons.CheckCircle className="w-4 h-4" /> : <Icons.AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Username</label>
          <input type="text" value={user?.username || ''} disabled className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Role</label>
          <input type="text" value={user?.role || ''} disabled className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 capitalize cursor-not-allowed" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
          <input type="text" value={profile.full_name} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
          <input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Company Name</label>
          <input type="text" value={profile.company_name} onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
        </div>
      </div>

      {/* Account Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="p-4 rounded-xl border border-gray-200" style={{ background: 'rgba(99, 102, 241, 0.02)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Icons.Zap className="w-4 h-4 text-indigo-500" />
            <span className="text-xs font-semibold text-gray-500 uppercase">TPS Limit</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{user?.tps_limit || 10}</p>
          <p className="text-xs text-gray-400">requests / second</p>
        </div>
        <div className="p-4 rounded-xl border border-gray-200" style={{ background: 'rgba(16, 185, 129, 0.02)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Icons.Calendar className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-semibold text-gray-500 uppercase">Member Since</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</p>
        </div>
        <div className="p-4 rounded-xl border border-gray-200" style={{ background: 'rgba(245, 158, 11, 0.02)' }}>
          <div className="flex items-center gap-2 mb-1">
            <Icons.Wallet className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-semibold text-gray-500 uppercase">Balance</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatIndianCurrency(user?.balance || 0)}</p>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-100">
        <button onClick={handleSave} disabled={loading}
          className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-all flex items-center gap-2 disabled:opacity-50">
          {loading ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Save className="w-4 h-4" />}
          Save Profile
        </button>
      </div>
    </div>
  );
};

// ============================================================
// CONTACTS TAB
// ============================================================
const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', designation: '', phone: '', email: '' });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [otpState, setOtpState] = useState({}); // { [contactId]: { sending, verifying, otpSent, otp, error } }

  const fetchContacts = useCallback(async () => {
    try {
      const res = await settingsAPI.getContacts();
      setContacts(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const resetForm = () => { setForm({ name: '', designation: '', phone: '', email: '' }); setEditId(null); setShowForm(false); };

  const handleSubmit = async () => {
    setMsg({ type: '', text: '' });
    try {
      if (editId) {
        await settingsAPI.updateContact(editId, form);
        setMsg({ type: 'success', text: 'Contact updated' });
      } else {
        await settingsAPI.createContact(form);
        setMsg({ type: 'success', text: 'Contact added' });
      }
      resetForm(); fetchContacts();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to save contact' });
    }
  };

  const handleEdit = (c) => {
    setForm({ name: c.name, designation: c.designation, phone: c.phone, email: c.email });
    setEditId(c.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await settingsAPI.deleteContact(id);
      fetchContacts();
    } catch (e) { console.error(e); }
  };

  const handleSendOTP = async (contact) => {
    if (!contact.phone) return;
    setOtpState(s => ({ ...s, [contact.id]: { ...s[contact.id], sending: true, error: '' } }));
    try {
      await settingsAPI.sendPhoneOTP(contact.id, contact.phone);
      setOtpState(s => ({ ...s, [contact.id]: { ...s[contact.id], sending: false, otpSent: true, otp: '' } }));
    } catch (e) {
      setOtpState(s => ({ ...s, [contact.id]: { ...s[contact.id], sending: false, error: e.response?.data?.error || 'Failed to send OTP' } }));
    }
  };

  const handleVerifyOTP = async (contact) => {
    const state = otpState[contact.id];
    if (!state?.otp) return;
    setOtpState(s => ({ ...s, [contact.id]: { ...s[contact.id], verifying: true, error: '' } }));
    try {
      await settingsAPI.verifyPhoneOTP(contact.id, contact.phone, state.otp);
      setOtpState(s => ({ ...s, [contact.id]: { otpSent: false, otp: '', verifying: false } }));
      fetchContacts();
      setMsg({ type: 'success', text: 'Phone number verified successfully!' });
    } catch (e) {
      setOtpState(s => ({ ...s, [contact.id]: { ...s[contact.id], verifying: false, error: e.response?.data?.error || 'Verification failed' } }));
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-5">
      {msg.text && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.type === 'success' ? <Icons.CheckCircle className="w-4 h-4" /> : <Icons.AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">Manage your contact persons. Phone numbers require OTP verification.</p>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5">
          <Icons.Plus className="w-4 h-4" /> Add Contact
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">{editId ? 'Edit Contact' : 'New Contact'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="Contact name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Designation</label>
              <input type="text" value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="e.g. Manager" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Phone</label>
              <input type="text" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="9876543210" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="contact@example.com" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={resetForm} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
              {editId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Contact List */}
      {contacts.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <Icons.Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No contacts added yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map(c => {
            const os = otpState[c.id] || {};
            return (
              <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{c.name}</h4>
                      {c.designation && <span className="text-xs text-gray-400">({c.designation})</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                      {c.phone && (
                        <div className="flex items-center gap-1.5">
                          <Icons.Phone className="w-3.5 h-3.5 text-gray-400" />
                          <span>{c.phone}</span>
                          {c.phone_verified ? (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] font-semibold rounded-full border border-green-200">
                              <Icons.CheckCircle className="w-3 h-3" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-semibold rounded-full border border-amber-200">
                              <Icons.Clock className="w-3 h-3" /> Pending
                            </span>
                          )}
                        </div>
                      )}
                      {c.email && (
                        <div className="flex items-center gap-1.5">
                          <Icons.Mail className="w-3.5 h-3.5 text-gray-400" />
                          <span>{c.email}</span>
                        </div>
                      )}
                    </div>

                    {/* OTP Verification Flow */}
                    {c.phone && !c.phone_verified && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {!os.otpSent ? (
                          <button onClick={() => handleSendOTP(c)} disabled={os.sending}
                            className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-semibold hover:bg-indigo-100 flex items-center gap-1 disabled:opacity-50">
                            {os.sending ? <Icons.Loader2 className="w-3 h-3 animate-spin" /> : <Icons.Send className="w-3 h-3" />}
                            Verify Phone
                          </button>
                        ) : (
                          <>
                            <input type="text" maxLength={6} placeholder="Enter OTP"
                              value={os.otp || ''} onChange={e => setOtpState(s => ({ ...s, [c.id]: { ...s[c.id], otp: e.target.value } }))}
                              className="w-28 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-center font-mono tracking-widest focus:ring-2 focus:ring-indigo-500" />
                            <button onClick={() => handleVerifyOTP(c)} disabled={os.verifying || !os.otp}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 flex items-center gap-1 disabled:opacity-50">
                              {os.verifying ? <Icons.Loader2 className="w-3 h-3 animate-spin" /> : <Icons.Check className="w-3 h-3" />}
                              Verify
                            </button>
                            <button onClick={() => handleSendOTP(c)} disabled={os.sending}
                              className="px-3 py-1.5 text-gray-500 text-xs hover:text-indigo-600">Resend</button>
                          </>
                        )}
                        {os.error && <span className="text-xs text-red-500">{os.error}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 ml-3">
                    <button onClick={() => handleEdit(c)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Edit">
                      <Icons.Pencil className="w-4 h-4 text-gray-400" />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg hover:bg-red-50" title="Delete">
                      <Icons.Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================
// IP WHITELIST TAB
// ============================================================
const IPWhitelistTab = () => {
  const [guiIPs, setGuiIPs] = useState([]);
  const [apiIPs, setApiIPs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ip_address: '', ip_type: 'gui', action_type: 'add', remarks: '' });
  const [msg, setMsg] = useState({ type: '', text: '' });

  const fetchData = useCallback(async () => {
    try {
      const [guiRes, apiRes, reqRes] = await Promise.all([
        settingsAPI.getIPWhitelist('gui'),
        settingsAPI.getIPWhitelist('api'),
        settingsAPI.getIPRequests(),
      ]);
      setGuiIPs(guiRes.data || []);
      setApiIPs(apiRes.data || []);
      setRequests(reqRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    setMsg({ type: '', text: '' });
    try {
      const res = await settingsAPI.createIPRequest(form);
      setMsg({ type: 'success', text: `Request ${res.data.request_number} submitted. Awaiting admin approval.` });
      setShowForm(false); setForm({ ip_address: '', ip_type: 'gui', action_type: 'add', remarks: '' });
      fetchData();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to submit request' });
    }
  };

  const statusBadge = (status) => {
    const colors = { pending: 'bg-amber-50 text-amber-700 border-amber-200', approved: 'bg-green-50 text-green-700 border-green-200', rejected: 'bg-red-50 text-red-700 border-red-200' };
    return <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${colors[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{status?.toUpperCase()}</span>;
  };

  if (loading) return <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  const IPTable = ({ title, icon, ips, type }) => (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
          <span className="text-xs text-gray-400">({ips.length})</span>
        </div>
      </div>
      {ips.length === 0 ? (
        <div className="p-6 text-center text-gray-400 text-sm">No {type} IPs whitelisted</div>
      ) : (
        <table className="w-full">
          <thead><tr className="bg-gray-50">
            <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">IP Address</th>
            <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Added</th>
            <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase">Actions</th>
          </tr></thead>
          <tbody>
            {ips.map(ip => (
              <tr key={ip.id} className="border-t border-gray-50">
                <td className="px-4 py-2.5 text-sm font-mono text-gray-800">{ip.ip_address}</td>
                <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(ip.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => { setForm({ ip_address: ip.ip_address, ip_type: type, action_type: 'delete', remarks: '' }); setShowForm(true); }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium">Request Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {msg.text && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.type === 'success' ? <Icons.CheckCircle className="w-4 h-4" /> : <Icons.AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">Request IP whitelisting for GUI access or HTTP API / SMPP bind.</p>
        <button onClick={() => { setForm({ ip_address: '', ip_type: 'gui', action_type: 'add', remarks: '' }); setShowForm(true); }}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5">
          <Icons.Plus className="w-4 h-4" /> Request IP
        </button>
      </div>

      {/* Request Form */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">IP Whitelist Request</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">IP Address *</label>
              <input type="text" value={form.ip_address} onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-indigo-500" placeholder="e.g. 103.21.58.100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Type *</label>
              <select value={form.ip_type} onChange={e => setForm(f => ({ ...f, ip_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                <option value="gui">GUI Access</option>
                <option value="api">HTTP API & SMPP Bind</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Action</label>
              <select value={form.action_type} onChange={e => setForm(f => ({ ...f, action_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                <option value="add">Add New IP</option>
                <option value="update">Update Existing IP</option>
                <option value="delete">Delete IP</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Remarks</label>
              <input type="text" value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="Optional notes" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
            <button onClick={handleSubmit} disabled={!form.ip_address}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">Submit Request</button>
          </div>
        </div>
      )}

      {/* IP Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <IPTable title="GUI Access IPs" icon={<Icons.Monitor className="w-4 h-4 text-indigo-500" />} ips={guiIPs} type="gui" />
        <IPTable title="API & SMPP IPs" icon={<Icons.Globe className="w-4 h-4 text-emerald-500" />} ips={apiIPs} type="api" />
      </div>

      {/* Request History */}
      {requests.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 text-sm">Request History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Request #</th>
                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">IP</th>
                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Action</th>
                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Date</th>
              </tr></thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id} className="border-t border-gray-50">
                    <td className="px-4 py-2 text-xs font-mono text-indigo-600">{r.request_number}</td>
                    <td className="px-4 py-2 text-xs font-mono text-gray-700">{r.ip_address}</td>
                    <td className="px-4 py-2 text-xs text-gray-600 uppercase">{r.ip_type}</td>
                    <td className="px-4 py-2 text-xs text-gray-600 capitalize">{r.action_type}</td>
                    <td className="px-4 py-2">{statusBadge(r.status)}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// API & SECURITY TAB
// ============================================================
const SecurityTab = ({ user, updateUser }) => {
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [keyLength, setKeyLength] = useState(16);
  const [msg, setMsg] = useState({ type: '', text: '' });

  // UI Mode state
  const [uiMode, setUiMode] = useState(user?.ui_mode || 'classic');
  const [uiLoading, setUiLoading] = useState(false);

  const maskApiKey = (key) => {
    if (!key) return '--------';
    return key.substring(0, 4) + '****' + key.substring(key.length - 4);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setApiKeyCopied(true);
    setTimeout(() => setApiKeyCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Are you sure? This will invalidate your current API key. All existing integrations will stop working.')) return;
    setRegenerating(true); setMsg({ type: '', text: '' });
    try {
      const res = await settingsAPI.regenerateAPIKey(keyLength);
      setNewKey(res.data.api_key);
      updateUser({ ...user, api_key: res.data.api_key });
      setMsg({ type: 'success', text: 'API key regenerated successfully. Copy your new key now!' });
      setShowApiKey(true);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to regenerate API key' });
    } finally { setRegenerating(false); }
  };

  const handleUIModeSave = async () => {
    setUiLoading(true);
    try {
      await authAPI.updateUIMode(uiMode);
      updateUser({ ...user, ui_mode: uiMode });
      setMsg({ type: 'success', text: 'UI mode updated! Page will reload...' });
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to update UI mode' });
    } finally { setUiLoading(false); }
  };

  const displayKey = newKey || user?.api_key;

  return (
    <div className="space-y-6">
      {msg.text && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.type === 'success' ? <Icons.CheckCircle className="w-4 h-4" /> : <Icons.AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* API Key Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Icons.Key className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">API Key</h3>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm text-gray-700">
            {showApiKey ? (displayKey || 'Not assigned') : maskApiKey(displayKey)}
          </div>
          <button onClick={() => setShowApiKey(!showApiKey)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title={showApiKey ? 'Hide' : 'Show'}>
            {showApiKey ? <Icons.EyeOff className="w-4 h-4 text-gray-500" /> : <Icons.Eye className="w-4 h-4 text-gray-500" />}
          </button>
          <button onClick={() => handleCopy(displayKey)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Copy">
            {apiKeyCopied ? <Icons.Check className="w-4 h-4 text-green-500" /> : <Icons.Copy className="w-4 h-4 text-gray-500" />}
          </button>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-500">Key Length:</label>
            <select value={keyLength} onChange={e => setKeyLength(Number(e.target.value))}
              className="px-2 py-1 border border-gray-200 rounded-lg text-xs">
              <option value={8}>8</option>
              <option value={16}>16</option>
              <option value={32}>32</option>
              <option value={64}>64</option>
            </select>
          </div>
          <button onClick={handleRegenerate} disabled={regenerating}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 flex items-center gap-1.5 disabled:opacity-50">
            {regenerating ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.RefreshCw className="w-3.5 h-3.5" />}
            Regenerate Key
          </button>
        </div>

        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-2">
            <Icons.AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">Regenerating your API key will immediately invalidate the old key. Update all your integrations with the new key.</p>
          </div>
        </div>
      </div>

      {/* Rate Limit Info */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Icons.Zap className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">Rate Limits</h3>
        </div>
        <div className="p-3 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
          <p className="text-xs text-indigo-600">
            SMS API endpoints are rate-limited to <strong>{user?.tps_limit || 10} requests/second</strong> per account.
            Exceeding this limit returns <code className="px-1 py-0.5 bg-indigo-50 rounded text-[10px]">429 Too Many Requests</code>.
            Contact support to increase your TPS limit.
          </p>
        </div>
      </div>

      {/* UI Mode */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Icons.Palette className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">User Interface</h3>
        </div>
        <div className="flex gap-3">
          {['classic', 'modern'].map(mode => (
            <div key={mode} onClick={() => setUiMode(mode)}
              className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all ${uiMode === mode ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${uiMode === mode ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                  {uiMode === mode && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                </div>
                <span className="font-semibold text-sm text-gray-900 capitalize">{mode} UI</span>
                {mode === 'modern' && <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-indigo-100 text-indigo-700 rounded-full">New</span>}
              </div>
            </div>
          ))}
        </div>
        {uiMode !== user?.ui_mode && (
          <div className="flex justify-end">
            <button onClick={handleUIModeSave} disabled={uiLoading}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50">
              {uiLoading ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Save className="w-4 h-4" />}
              Apply UI Mode
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// MAIN SETTINGS PAGE
// ============================================================
const Settings = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="max-w-5xl">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}>
            <Icons.Settings className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p className="text-sm text-gray-500">Manage your profile, contacts, security, and preferences</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="flex overflow-x-auto" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          {tabs.map(tab => {
            const Icon = Icons[tab.icon] || Icons.Settings;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-indigo-600 border-indigo-600 bg-indigo-50/50'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                }`}>
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {activeTab === 'profile' && <ProfileTab user={user} updateUser={updateUser} />}
        {activeTab === 'contacts' && <ContactsTab />}
        {activeTab === 'ip-whitelist' && <IPWhitelistTab />}
        {activeTab === 'security' && <SecurityTab user={user} updateUser={updateUser} />}
      </div>
    </div>
  );
};

export default Settings;
